import { api } from './client'

export interface ScopeEntry {
  id: string
  project_id: string
  entry_type: 'domain' | 'cidr'
  value: string
  scope_action: 'include' | 'exclude'
  source: 'manual' | 'approved_proposal' | 'netbox_import'
  note?: string
  description?: string
  tags?: string[]
  sync_job_id?: string
  disabled_at?: string
  disabled_reason?: string
  disabled_by?: 'sync' | 'user' | 'unresolved' | ''
  created_at: string
  updated_at: string
}

export interface ScopeResponse {
  entries: ScopeEntry[]   // combined sorted list (CIDR then domains, hierarchical)
  includes: ScopeEntry[]  // subset for badge counts
  excludes: ScopeEntry[]  // subset for badge counts
  disabled: ScopeEntry[]  // deactivated entries (sync or user), shown separately
  assets: ScopeAssetLink[] // domain ↔ IP from scan_scope_assets
}

export interface ScopeAssetLink {
  domain: string
  ip: string
  scanner_name: string
  last_seen_at: string
}

// Вторая половина пары «домен ↔ адрес»: у доменного предложения — адреса, в
// которые оно резолвится, у адресного — домены, которые в него ведут.
// Выводится сервером из scan_scope_assets на чтении, в предложении не хранится.
export interface ProposalCounterpart {
  entry_type: 'domain' | 'cidr'
  value: string
  // Значение уже допущено активным скоупом — отмечать нечего.
  in_scope: boolean
  // Другие значения, завязанные на ту же половину. Для ИСКЛЮЧЕНИЯ это
  // предупреждение: выключив адрес, который резолвят ещё чьи-то домены,
  // оператор снимет покрытие и с них.
  shared_with?: string[]
  last_seen_at: string
}

export interface ScopeProposal {
  id: string
  project_id: string
  entry_type: 'domain' | 'cidr'
  proposed_value: string
  modified_value?: string
  // Направление предложения. Сканеры шлют только 'include'; 'exclude' приходит
  // от NetBox-синхронизации, когда адрес за доменом освобождён в IPAM.
  // Необязательное — backend старее этого поля просто его не пришлёт, и тогда
  // предложение трактуется как расширение (прежнее поведение).
  proposed_action?: 'include' | 'exclude'
  // Причина — заполняется у предложений на сужение: какой адрес и в каком
  // статусе. Без неё оператор не может решить, не открывая NetBox руками.
  reason?: string
  status: 'pending' | 'approved' | 'rejected'
  scanner_name: string
  source_domain?: string
  source_ip?: string
  trail?: object
  reviewed_by?: string
  reviewed_at?: string
  created_at: string
  updated_at?: string
  // Provenance fields from Task 4 — populated when the proposal matches a
  // previously-disabled scope entry (backend reconciliation).
  previously_disabled?: boolean
  prior_disabled_by?: string        // "user" | "sync"
  prior_disabled_reason?: string
  prior_scope_action?: 'include' | 'exclude'
  counterparts?: ProposalCounterpart[]
  // Сколько связок нашлось ВСЕГО. Больше длины counterparts — значит показаны
  // не все, и об этом надо сказать: иначе список читается как исчерпывающий.
  counterparts_total?: number
}

export interface ScopeAsset {
  id: string
  project_id: string
  domain: string
  ip: string
  scanner_name: string
  trail?: object
  first_seen_at: string
  last_seen_at: string
}

export interface SyncJob {
  id: string
  project_id: string
  name: string
  api_endpoint: string
  tls_skip_verify: boolean
  allow_private_ips: boolean
  tenant?: string
  ip_tags?: string[]
  exclude_tags?: string[]
  dns_view?: string
  scope_action: 'include' | 'exclude'
  schedule_interval_hours?: number
  schedule_cron?: string
  enabled: boolean
  active_statuses: string[]
  auto_remove: boolean
  network_type: 'public' | 'private' | 'all'
  ip_tags_mode: 'any' | 'all'
  exclude_tags_mode: 'any' | 'all'
  include_tenants?: string[]
  exclude_tenants?: string[]
  include_tenants_mode: 'any' | 'all'
  exclude_tenants_mode: 'any' | 'all'
  import_tags: boolean
  last_run_at?: string
  last_run_status?: 'success' | 'partial' | 'error'
  last_run_result?: object
  next_run_at?: string
  has_api_token: boolean
  is_running: boolean
  created_at: string
}

export interface NetBoxImportRequest {
  api_endpoint: string
  api_token: string
  // Отключение TLS-сертификат verification (для self-signed CA).
  tls_skip_verify?: boolean
  // Отключение SSRF-защиты (для NetBox в private VPN).
  // Cloud metadata endpoints блокируются всегда.
  allow_private_ips?: boolean
  tenant?: string
  ip_tags?: string[]
  exclude_tags?: string[]
  dns_view?: string
  scope_action: 'include' | 'exclude'
  import_tags?: boolean
}

export interface ImportResult {
  imported: number
  skipped: number
  disabled: number
  removed: number
  reactivated: number
  total_fetched: number
}

// Explicit allowlist of fields that callers may update — prevents accidental writes to server-managed fields
export interface SyncJobUpdateRequest {
  name?: string
  enabled?: boolean
  tls_skip_verify?: boolean
  allow_private_ips?: boolean
  tenant?: string
  ip_tags?: string[]
  exclude_tags?: string[]
  dns_view?: string
  scope_action?: 'include' | 'exclude'
  schedule_interval_hours?: number
  schedule_cron?: string
  active_statuses?: string[]
  auto_remove?: boolean
  network_type?: 'public' | 'private' | 'all'
  ip_tags_mode?: 'any' | 'all'
  exclude_tags_mode?: 'any' | 'all'
  include_tenants?: string[]
  exclude_tenants?: string[]
  include_tenants_mode?: 'any' | 'all'
  exclude_tenants_mode?: 'any' | 'all'
  import_tags?: boolean
  // Если заполнено — API-токен будет обновлён; пустая строка/undefined = не менять.
  api_token?: string
}

export interface ScopeImportRowError {
  row: number
  value: string
  error: string
}

export interface ScopeImportResult {
  created: number
  skipped: number
  failed: number
  errors: ScopeImportRowError[]
}

export const scopeApi = {
  // signal — запрос ходит на каждый шаг поиска по периметру, где записей
  // тысячи; без отмены устаревший доезжает до конца (#398).
  getScope: (projectId: string, search?: string, signal?: AbortSignal) =>
    api.get<ScopeResponse>(`/projects/${projectId}/scope`, {
      params: search ? { search } : undefined,
      signal,
    }),

  addEntry: (
    projectId: string,
    body: { entry_type: string; value: string; scope_action: string; note?: string }
  ) => api.post<ScopeEntry>(`/projects/${projectId}/scope/entries`, body),

  importEntries: (
    projectId: string,
    entries: Array<{ entry_type?: string; value: string; scope_action: string; tags: string[]; description: string }>
  ) =>
    api.post<ScopeImportResult>(`/projects/${projectId}/scope/entries/import`, { entries }),

  updateEntry: (
    projectId: string,
    entryId: string,
    body: { value?: string; scope_action?: string; note?: string }
  ) => api.patch(`/projects/${projectId}/scope/entries/${entryId}`, body),

  deleteEntry: (projectId: string, entryId: string) =>
    api.delete(`/projects/${projectId}/scope/entries/${entryId}`),

  restoreEntry: (projectId: string, entryId: string) =>
    api.post(`/projects/${projectId}/scope/entries/${entryId}/restore`),

  purgeEntry: (projectId: string, entryId: string) =>
    api.delete(`/projects/${projectId}/scope/entries/${entryId}/purge`),

  bulkDeleteEntries: (projectId: string, ids: string[]) =>
    api.post<{ affected: number }>(`/projects/${projectId}/scope/entries/bulk-delete`, { ids }),

  bulkUpdateScopeAction: (projectId: string, ids: string[], scopeAction: 'include' | 'exclude') =>
    api.post<{ affected: number }>(`/projects/${projectId}/scope/entries/bulk-action`, {
      ids,
      scope_action: scopeAction,
    }),

  listProposals: (projectId: string, status?: string) =>
    api.get<ScopeProposal[]>(`/projects/${projectId}/scope/proposals`, {
      params: status ? { status } : {},
    }),

  reviewProposal: (
    projectId: string,
    proposalId: string,
    body: {
      action: string
      modified_value?: string
      scope_action?: string
      counterparts?: Array<{ entry_type: string; value: string }>
    }
  ) => api.put(`/projects/${projectId}/scope/proposals/${proposalId}`, body),

  batchReviewProposals: (projectId: string, body: { proposal_ids: string[]; action: 'approve' | 'reject'; scope_action?: string }) =>
    api.post(`/projects/${projectId}/scope/proposals/batch-review`, body),

  listAssets: (
    projectId: string,
    params?: { domain?: string; ip?: string; page?: number; per_page?: number },
    signal?: AbortSignal
  ) =>
    api.get<{ data: ScopeAsset[]; total: number }>(
      `/projects/${projectId}/scope/assets`,
      { params, signal }
    ),

  importNetBox: (projectId: string, body: NetBoxImportRequest) =>
    api.post<ImportResult>(`/projects/${projectId}/scope/import/netbox`, body),

  listSyncJobs: (projectId: string) =>
    api.get<SyncJob[]>(`/projects/${projectId}/scope/sync-jobs`),

  createSyncJob: (
    projectId: string,
    body: Omit<SyncJob, 'id' | 'project_id' | 'created_at' | 'has_api_token'> & { api_token: string }
  ) => api.post<SyncJob>(`/projects/${projectId}/scope/sync-jobs`, body),

  updateSyncJob: (projectId: string, jobId: string, body: SyncJobUpdateRequest) =>
    api.patch(`/projects/${projectId}/scope/sync-jobs/${jobId}`, body),

  triggerSyncJob: (projectId: string, jobId: string) =>
    api.post(`/projects/${projectId}/scope/sync-jobs/${jobId}/run`),

  deleteSyncJob: (projectId: string, jobId: string) =>
    api.delete(`/projects/${projectId}/scope/sync-jobs/${jobId}`),

  getPendingProposalsCounts: () =>
    api.get<Record<string, number>>('/scope/proposals/pending-counts'),

  // Активные include/exclude счётчики по всем доступным проектам одним запросом.
  // Проекты без активных entries в ответе отсутствуют (== скоуп не настроен).
  getScopeSummaryCounts: () =>
    api.get<Record<string, ScopeSummaryCount>>('/scope/summary-counts'),
}

export interface ScopeSummaryCount {
  includes: number
  excludes: number
}
